import type { PermissionFormDetails } from '@/data/code-sessions/permission-form';
import type {
  CodePromptAttachment,
  CodeTurnDelivery,
  PendingPrompt,
} from '@/data/code-sessions/types';
import type { AgentPage } from '@/lib/code-sessions/agent-page';
import type { AiSubscriptionErrorOffer } from '@/lib/code-sessions/ai-subscriptions/errors';
import type { AiSubscriptionProvider } from '@/lib/code-sessions/ai-subscriptions/providers';
import type { EnvironmentSuggestion } from '@/lib/code-sessions/environment-suggestion';
import type { CodeSharedFileSummary } from '@/lib/code-sessions/shared-file-types';
import type { ToolStepKind } from '@/lib/code-sessions/tool-steps';
import type { CodeSessionAgentEvent, CodeSessionEvent } from '@/lib/code-sessions/types';
import type {
  FileDiffInfo,
  FormField,
  FormInfo,
  SessionMessageAssistant,
  SessionMessageInfo,
} from '@opencode/client/promise';

import type { BootPhaseState, SandboxBoot } from './boot-phase';
import type { AgentPagePart } from './transcript-agent-page';
import type { CodeModeScript } from './transcript-code-mode';
import type { EnvironmentSuggestionPart } from './transcript-environment-suggestion';
import type { ResponseRetryStatus } from './transcript-retries';
import type { TimelineAnchorOwner } from './transcript-rewind';
import type { SubagentActivities, SubagentCall } from './transcript-subagents';

import { foldAgentPageEvent, liveAgentPagePart, savedAgentPagePart } from './transcript-agent-page';
import {
  recordCodeModeScript,
  seedCodeModeScripts,
  showCodeModeScripts,
} from './transcript-code-mode';
import {
  foldEnvironmentSuggestionEvent,
  liveEnvironmentSuggestionPart,
  savedEnvironmentSuggestionPart,
} from './transcript-environment-suggestion';
import {
  isUncheckedFindingsReport,
  markKeptFindings,
  savedFindingsPart,
  STEPLESS_LIVE_TOOLS,
} from './transcript-findings';
import { responseRetryFold, successfulResponseFinish } from './transcript-retries';
import { visibleTimelineAnchors } from './transcript-rewind';
import {
  foldSubagentEvent,
  noteSubagentActivity,
  savedSubagentCall,
  showSubagentActivity,
  subagentFailure,
} from './transcript-subagents';

/**
 * Pure transcript fold for /code: turn rows + OpenCode messages + normalised
 * agent events → a single linear timeline of `SessionPart`s. No I/O; the hook
 * supplies inputs and components stay presentational. The timeline interleaves
 * user bubbles (from turn rows), persisted assistant content, and live
 * event-built parts in time order so the chat reads like a standard LLM chat.
 */
import { eventData, streamKeyOf } from '@/data/code-sessions/agent-events';
import { permissionFormDetails } from '@/data/code-sessions/permission-form';
import { isPersonTurn, userMessageText } from '@/data/code-sessions/turn-prompt';
import { aiSubscriptionErrorView } from '@/lib/code-sessions/ai-subscriptions/errors';
import { ENVIRONMENT_SUGGESTION_TOOL } from '@/lib/code-sessions/environment-suggestion';
import { DECLINE_NOTE } from '@/lib/code-sessions/local-agent/approval-replies';
import { OPEN_PULL_REQUEST_TOOL } from '@/lib/code-sessions/session-tools/open-pull-request';
import { PR_PAGE_TOOL_NAMES } from '@/lib/code-sessions/session-tools/pr-page';
import { SHOW_PAGE_TOOL } from '@/lib/code-sessions/session-tools/show-page';
import { SUBAGENT_ACTIVITY_EVENT } from '@/lib/code-sessions/subagent-activity';
import {
  COMMAND_TOOL_NAMES,
  isPushCommand,
  QUESTION_TOOL_NAME,
  stringInput,
  SUBMIT_BATCH_TOOL_NAME,
  SUBMIT_RESULT_TOOL_NAME,
  toolStepDetail,
  toolStepKind,
  toolStepLabel,
} from '@/lib/code-sessions/tool-steps';
import { turnFailureWords } from '@/lib/code-sessions/turn-failure-words';
import {
  deterministicPromptId,
  isTerminalTurnStatus,
  isUndeliveredTurnStatus,
} from '@/lib/code-sessions/turn-utils';
import { OPENCODE_CODE_MODE_TOOL_NAME } from '@/lib/mcp-servers/tool-names';
import { getRecord } from '@/lib/utils/unknown';

type AssistantContent = SessionMessageAssistant['content'][number];

export type TraceStepKind = ToolStepKind;

export type TraceStep = {
  kind: TraceStepKind;
  /** Short human label, e.g. "Read", "Ran". */
  label: string;
  /** Path, command, or search shown next to the action label. */
  detail: string;
  /** Present only when this step names one file that can be opened. */
  openPath?: string;
  /** Why the call failed; available in the row's error disclosure. */
  error?: string;
  /** Tool stdout/edit output, capped; expandable in the UI. */
  output?: string;
  /** What an edit or patch call changed, one entry per file. */
  diffs?: TraceStepFileDiff[];
  running?: boolean;
  failed?: boolean;
  /** Cut short by Stop; shown quietly rather than as a failure. */
  interrupted?: boolean;
  /** The person declined the call; the step carries the state alone, OpenCode's reason only repeats it. */
  denied?: boolean;
  /** The call waits for the person's approval, which the question card takes. */
  waitingApproval?: boolean;
  /** OpenCode's id for the call, which a permission request names. */
  callId?: string;
  /**
   * Asked for a path that doesn't exist. The agent reads the error and moves
   * on, so it's shown quietly rather than as a failure.
   */
  notFound?: boolean;
  /**
   * When the call began running: saved `time.ran ?? time.created`, unset while the model still
   * writes it; live its `session.tool.called`. Epoch ms.
   */
  startedAt?: number;
  /** When the call settled: saved `time.completed`; live its success or failure. Epoch ms. */
  endedAt?: number;
  /** Present only for shell commands, which render as a terminal card. */
  command?: ShellCommandStatus;
  /** Code Mode's script and its individual calls, including HTTP fetches and repeated tools. */
  codeMode?: CodeModeScript;
  /**
   * Present only for subagent calls: the child session whose transcript the step opens, once
   * OpenCode names it, and the first line of its report. Whether it still runs, failed or was
   * stopped, and when it ran, are the step's own, as for any step.
   */
  subagent?: SubagentCall;
  /**
   * Present only for a pull request page's tools (`session-tools/pr-page.ts`): the call as the
   * page's chat reads it, to act on it and show it as its own chat's tools. `output` is the
   * call's whole result, uncapped, since the page reads it as data.
   */
  call?: TraceStepCall;
};

export type TraceStepCall = { id: string; name: string; input?: unknown; output?: string };

/**
 * One file an edit or patch call changed, as OpenCode's `FileDiff.Info` reports
 * it. `patch` is that call's change alone, capped like every event string.
 */
export type TraceStepFileDiff = Pick<FileDiffInfo, 'file' | 'patch' | 'additions' | 'deletions'>;

export type ShellCommandStatus = {
  exitCode?: number;
  timedOut?: boolean;
  durationMs?: number;
};

export type SessionQuestionOption = { value: string; label: string; description?: string };

export type SessionQuestionField = {
  key: string;
  prompt: string;
  /** The question's short header, as the dock titles it; absent when the question has none besides its prompt. */
  header?: string;
  options: string[];
  optionValues?: SessionQuestionOption[];
  freeText?: boolean;
  multiple?: boolean;
  required?: boolean;
  custom?: boolean;
};

/**
 * A steer the agent has not taken yet. `interrupting`: this tab asked the
 * agent to cut in for it. `waiting`: it waits for the agent's next step.
 */
export type PendingSteer = { turnId: string; kind: 'interrupting' | 'waiting' };

type SessionPartContent =
  | {
      type: 'user';
      text: string;
      authoredText?: string | null;
      /** Files sent with the prompt; absent when none were. */
      attachments?: readonly CodePromptAttachment[];
      /** Set on a steer until the agent takes it. */
      pending?: PendingSteer;
      /** Set when cubic sent this prompt for a scheduled check-in; the row shows it instead of the text. */
      checkInReason?: string;
      /** Set when another agent of the session sent this prompt: its name. The row shows the message it wrote. */
      messageFrom?: string;
      /** Who wrote the prompt; unset on one no turn saved, such as a forked session's history. */
      authorProfileId?: string;
    }
  | { type: 'reasoning'; text: string; durationSeconds: number; running?: boolean }
  | { type: 'trace'; steps: TraceStep[]; running?: boolean }
  | { type: 'text'; markdown: string }
  | {
      type: 'question';
      prompt: string;
      options: string[];
      formId?: string;
      fields: SessionQuestionField[];
      /**
       * Set when the form stands for a permission request the agent waits on, in a session on the
       * person's own computer: the permission card takes the answer in the page, Allow once,
       * Allow for this session or Deny (`question-card.tsx`).
       */
      permission?: PermissionFormDetails;
    }
  | {
      type: 'status';
      label: string;
      running?: boolean;
      retry?: ResponseRetryStatus;
      /**
       * While a response is retried: why the last attempt failed, in cubic's words from its status
       * and kind, never the provider's own (`transcript-retries.ts`); absent where neither says.
       */
      reason?: string;
      /** While a response is retried: when OpenCode makes the next attempt, in epoch milliseconds. */
      nextAttemptAt?: number;
    }
  | {
      /** A `suggest_environment` call, shown as a card to review and save. */
      type: 'environment_suggestion';
      /** The tool call's id; the environment check started from the card is keyed by it. */
      callId: string;
      /** Null until a saved message carries the call's input; live events never fill it. */
      suggestion: EnvironmentSuggestion | null;
      running?: boolean;
    }
  | {
      /** A `show_page` call: a page the agent made, shown in a sandboxed frame (`agent-page-frame.tsx`). */
      type: 'page';
      /** The tool call's id. */
      callId: string;
      /** Null until a whole copy of the call's input is in: live events cut long strings and hide what looks like a secret. */
      page: AgentPage | null;
      running?: true;
    }
  | {
      /**
       * A `report_findings` call that recorded findings, shown as a card from
       * which the person can have the agent remove them (`transcript-findings.ts`).
       */
      type: 'findings';
      /** The tool call's id. */
      callId: string;
      /** The merged pull request the findings are about. */
      pullRequest: number;
      /** The default-branch commit the agent searched; each finding's line is in it. */
      commit: string;
      /** The agent's plain summary of what the merge left behind, when this call gave one. */
      summary?: string;
      findings: ReadonlyArray<{
        name: string;
        path: string;
        line: number;
        reason: string;
        /** How the agent confirmed nothing uses it, which the card shows under the reason. */
        evidence: string;
        /** A few plain words naming it, which the card shows in place of its name, when the agent gave them. */
        label?: string;
        /** What removing it takes away, in plain words, when the agent said. */
        change?: string;
        /** A later call recorded it as not dead code, so the card's button leaves it out. */
        kept?: true;
      }>;
    }
  | {
      type: 'boot';
      phases: readonly BootPhaseState[];
      /** The boot of a sandbox cubic replaced since: its card takes no work. */
      replaced?: true;
    }
  | {
      /**
       * Where the session's pull request shows: the end of the latest turn that either
       * pushed the session's branch or called `open_pull_request` to open the pull
       * request. A later turn of either kind moves it, so a transcript has at most one
       * result.
       */
      type: 'result';
      turnId: string;
    }
  | {
      /** The screenshots and videos a turn's `share_file` calls shared, or why each was not. */
      type: 'shared_files';
      turnId: string;
      files: readonly CodeSharedFileSummary[];
    }
  | {
      type: 'error';
      message: string;
      turnId?: string;
      /** Set when the turn failed on a subscription: what its card offers (`aiSubscriptionErrorView`). */
      aiSubscription?: AiSubscriptionErrorOffer;
    };

export type SessionPart = SessionPartContent & {
  /** Message creation time in epoch milliseconds; live text uses its first event until saved. */
  createdAt?: number;
  /** Stable within a message across stream and snapshot replacement. */
  id?: string;
  messageId?: string;
  /** Saved OpenCode message completion; absent from live deltas until snapshot replacement. */
  messageFinish?: string;
  messageCompletedAt?: number;
  turn?: { id: string; status: string; startedAt?: number; endedAt?: number };
  /** This is the latest known running turn, not a queued follow-up. */
  active?: boolean;
};

type TurnEvent = Extract<CodeSessionEvent, { kind: 'turn' }>;

/** Control-plane turn: the session row's turn shape (dates as epoch ms). */
export type TimelineTurn = {
  id: string;
  index: number;
  prompt: string;
  authoredText?: string | null;
  attachments: readonly CodePromptAttachment[];
  inboxId: string | null;
  status: string;
  /** A steer goes into the run going on; a queued prompt waits for that run to end. */
  delivery: CodeTurnDelivery;
  createdAt: number;
  startedAt?: number;
  /** When cubic claimed it for its send to the agent, from its first `admitted` event. */
  claimedAt?: number;
  endedAt?: number | null;
  errorMessage?: string | null;
  /** What the turn's `share_file` calls shared, once it ended. */
  sharedFiles?: readonly CodeSharedFileSummary[];
  /** Set once a committed rewind discarded the turn. */
  discardedAt?: string | null;
  /** Set on a turn cubic sent for a scheduled check-in. */
  checkInReason: string | null;
  /** Set on a turn another agent of the session sent with `send_message` or `followup_task`. */
  messageFrom: string | null;
  /** Who wrote the turn; unset on this tab's own prompt while it does not know its viewer. */
  authorProfileId?: string;
};

export type BuildTimelineInput = {
  turns?: readonly TimelineTurn[];
  messages: readonly SessionMessageInfo[];
  agentEvents: readonly CodeSessionAgentEvent[];
  turnEvents?: readonly TurnEvent[];
  forms?: readonly FormInfo[];
  /** One boot per sandbox the session had, the current sandbox's last (`foldSandboxBoots`). */
  boots?: readonly SandboxBoot[];
  pendingPrompts?: readonly PendingPrompt[];
  /** Local submission times keep bubbles in place when server content replaces them. */
  promptCreatedAt?: Readonly<Record<string, number>>;
  /** Saved turns the transcript hides: discarded ones, and a waiting rewind's boundary and every later one. */
  hidden?: ReadonlySet<string>;
  /** Steers the agent has not taken yet, by turn ID. */
  pendingSteers?: ReadonlyMap<string, PendingSteer>;
  /** Who this tab's pending prompts are by, as the session saves its prompts (`SessionConversation.promptAuthor`). */
  promptAuthorId?: string;
  /**
   * The subscription the session runs on, whose codes shared with other APIs its error cards
   * then read too (`aiSubscriptionErrorView`).
   */
  aiSubscriptionProvider?: AiSubscriptionProvider;
};

const TOOL_OUTPUT_MAX_BYTES = 4 * 1024;

function capOutput(text: string): string {
  return text.length <= TOOL_OUTPUT_MAX_BYTES
    ? text
    : `${text.slice(0, TOOL_OUTPUT_MAX_BYTES)}\n…[truncated]`;
}

/** Uncapped plain text out of a tool result `content` array (state.completed/event). */
function toolContentText(content: unknown): string | undefined {
  if (!Array.isArray(content)) {
    return undefined;
  }
  const text = content
    .map((part) =>
      typeof part === 'object' && part !== null
        ? ((part as { text?: unknown }).text ?? '')
        : typeof part === 'string'
          ? part
          : '',
    )
    .filter((value): value is string => typeof value === 'string' && value.length > 0)
    .join('\n');
  return text.length > 0 ? text : undefined;
}

/** Plain text out of a tool result `content` array (state.completed/event). */
function toolOutputText(content: unknown): string | undefined {
  const text = toolContentText(content);
  return text === undefined ? undefined : capOutput(text);
}

/**
 * A finished call's output, which its step shows when expanded. A subagent step
 * opens the child's transcript instead, which ends with the same report.
 */
function finishedStepOutput(kind: TraceStepKind, content: unknown): string | undefined {
  return kind === 'agent' ? undefined : toolOutputText(content);
}

/**
 * OpenCode 2.0.26's Code Mode `execute` completes even when its script fails:
 * its metadata then carries `error: true`, and its content is the error.
 */
function failedCodeModeError(
  toolName: string,
  content: unknown,
  metadata: unknown,
): string | undefined {
  if (toolName !== OPENCODE_CODE_MODE_TOOL_NAME || getRecord(metadata)?.error !== true) {
    return undefined;
  }
  return toolOutputText(content) ?? 'Failed';
}

// OpenCode 2.0.11 wrote "Command exited with code N." and "Command timed out
// before completion."; 2.0.12 shortened them to "Exited with code N" (nothing
// for exit 0) and "Timed out before completion". Saved sessions keep either.
const SHELL_EXIT_NOTICE =
  /(?:^|\n+)(?:(?:Command exited|Exited) with code (-?\d+)\.?|((?:Command timed|Timed) out before completion)\.?)\s*$/;

/**
 * OpenCode's shell tool appends a model-facing notice as its last content part
 * ("Exited with code N" or "Timed out before completion", in either version's
 * wording). Split it off so the card shows the status instead of that line.
 */
export function splitShellExitNotice(text: string): { output: string } & ShellCommandStatus {
  const match = SHELL_EXIT_NOTICE.exec(text);
  if (match === null) {
    return { output: text };
  }
  const output = text.slice(0, match.index);
  if (match[2] !== undefined) {
    return { output, timedOut: true };
  }
  return { output, exitCode: Number(match[1]) };
}

/**
 * The shell tool's result metadata carries `exit` and `timeout` in every
 * version, including exit 0, which newer notices no longer mention. Undefined
 * when a result carries neither, leaving the notice as the only status.
 */
function shellMetadataStatus(metadata: unknown): ShellCommandStatus | undefined {
  if (typeof metadata !== 'object' || metadata === null) {
    return undefined;
  }
  const { exit, timeout } = metadata as { exit?: unknown; timeout?: unknown };
  if (typeof exit !== 'number' && timeout !== true) {
    return undefined;
  }
  return {
    ...(typeof exit === 'number' ? { exitCode: exit } : {}),
    ...(timeout === true ? { timedOut: true } : {}),
  };
}

function shellCommandOutput(
  content: unknown,
  metadata: unknown,
): { output?: string } & ShellCommandStatus {
  const status = shellMetadataStatus(metadata);
  const text = toolContentText(content);
  if (text === undefined) {
    return status ?? {};
  }
  const { output, ...notice } = splitShellExitNotice(text);
  if (status === undefined) {
    return { output: capOutput(output), ...notice };
  }
  // The metadata is authoritative. OpenCode's notice only restates it, so a last
  // line that disagrees, such as "Exited with code 3" printed by a command that
  // exited 0, is the command's own output.
  const isOpenCodeNotice =
    notice.timedOut === true ? status.timedOut === true : notice.exitCode === status.exitCode;
  return { output: capOutput(isOpenCodeNotice ? output : text), ...status };
}

/** How long a settled command ran, from its step's clock. */
function commandDuration({
  startedAt,
  endedAt,
}: Pick<TraceStep, 'startedAt' | 'endedAt'>): Pick<ShellCommandStatus, 'durationMs'> {
  return startedAt === undefined || endedAt === undefined
    ? {}
    : { durationMs: Math.max(0, endedAt - startedAt) };
}

/** The files an edit or patch call changed, from OpenCode's `metadata.files` (`FileDiff.Info`). */
function toolStepDiffs(metadata: unknown): TraceStepFileDiff[] | undefined {
  if (typeof metadata !== 'object' || metadata === null) {
    return undefined;
  }
  const files = (metadata as { files?: unknown }).files;
  if (!Array.isArray(files)) {
    return undefined;
  }
  const diffs = files.flatMap((entry: unknown): TraceStepFileDiff[] => {
    if (typeof entry !== 'object' || entry === null) {
      return [];
    }
    const { file, patch, additions, deletions } = entry as Record<string, unknown>;
    return typeof file === 'string' &&
      typeof patch === 'string' &&
      typeof additions === 'number' &&
      typeof deletions === 'number'
      ? [{ file, patch, additions, deletions }]
      : [];
  });
  return diffs.length > 0 ? diffs : undefined;
}

function toolTraceStep(content: Extract<AssistantContent, { type: 'tool' }>): TraceStep {
  const kind = toolStepKind(content.name);
  const input = 'input' in content.state ? content.state.input : undefined;
  const running = content.state.status === 'streaming' || content.state.status === 'running';
  const isCommand = COMMAND_TOOL_NAMES.has(content.name);
  const metadata = 'metadata' in content.state ? content.state.metadata : undefined;
  const { output: commandOutput, ...commandStatus } =
    isCommand && (content.state.status === 'completed' || content.state.status === 'error')
      ? shellCommandOutput(content.state.content, metadata)
      : {};
  const output = isCommand
    ? commandOutput
    : content.state.status === 'completed' || content.state.status === 'error'
      ? finishedStepOutput(kind, content.state.content)
      : undefined;
  // A call saved while the model still wrote it has not started.
  const startedAt =
    content.state.status === 'streaming' ? undefined : (content.time.ran ?? content.time.created);
  const endedAt = content.time.completed;
  const failure: StepFailure =
    content.state.status === 'error' ? failedStep(kind, content.state.error) : {};
  const codeModeError =
    content.state.status === 'completed'
      ? failedCodeModeError(content.name, content.state.content, metadata)
      : undefined;
  return {
    kind,
    label: toolStepLabel(content.name),
    ...toolStepDetail(content.name, input),
    running: running ? true : undefined,
    failed: failure.failed === true || codeModeError !== undefined ? true : undefined,
    interrupted: failure.interrupted,
    denied: failure.denied,
    callId: content.id,
    notFound: failure.notFound,
    error: codeModeError ?? failure.error,
    output: codeModeError !== undefined ? undefined : output,
    diffs: toolStepDiffs(metadata),
    startedAt,
    endedAt,
    command: isCommand
      ? {
          ...commandStatus,
          // A declined command never ran: its clock holds only the wait for the person's answer,
          // which its row must not show as the command's time.
          ...(failure.denied === true ? {} : commandDuration({ startedAt, endedAt })),
        }
      : undefined,
    subagent: kind === 'agent' ? savedSubagentCall(content) : undefined,
    call: pageToolCall(content, input),
  };
}

/** A pull request page's tool call as its step keeps it (`TraceStep.call`); undefined for any other tool. */
function pageToolCall(
  content: Extract<AssistantContent, { type: 'tool' }>,
  input: unknown,
): TraceStepCall | undefined {
  if (!PR_PAGE_TOOL_NAMES.has(content.name)) {
    return undefined;
  }
  return {
    id: content.id,
    name: content.name,
    input,
    output:
      content.state.status === 'completed' ? toolContentText(content.state.content) : undefined,
  };
}

type TranscriptError = Pick<Extract<SessionPart, { type: 'error' }>, 'message' | 'aiSubscription'>;

/**
 * A turn, step or response failure as its card shows it: a subscription's own failure by the code
 * it carries, before OpenCode's generic type could call a usage limit a busy model, else
 * `errorMessageOf`. Tool steps keep `errorMessageOf`: a command's output can quote any code.
 * `ctx` names the subscription the session runs on.
 */
function transcriptErrorOf(
  error: unknown,
  ctx: Pick<FoldContext, 'aiSubscriptionProvider'>,
): TranscriptError | undefined {
  const subscription = aiSubscriptionErrorView(error, ctx.aiSubscriptionProvider);
  if (subscription !== null) {
    const { message, ...offer } = subscription;
    return { message, aiSubscription: offer };
  }
  const message = errorMessageOf(error);
  return message === undefined ? undefined : { message };
}

function errorMessageOf(error: unknown): string | undefined {
  if (typeof error === 'string') {
    return error;
  }
  if (typeof error === 'object' && error !== null) {
    const message = (error as { message?: unknown }).message;
    const type = 'type' in error ? error.type : undefined;
    // OpenCode classifies provider failures before emitting them. Use that
    // stable type for live events and saved messages, not provider wording.
    if (type === 'provider.rate-limit') {
      return 'The AI model is busy right now. Please try again in a moment.';
    }
    // The pinned OpenCode build loses GatewayRateLimitError's HTTP status and
    // emits provider.unknown. Keep this fallback until it preserves that type.
    if (
      type === 'provider.unknown' &&
      typeof message === 'string' &&
      message.startsWith('The request limited providers for this model') &&
      message.includes('currently at capacity')
    ) {
      return 'The AI model is busy right now. Please try again in a moment.';
    }
    if (typeof message === 'string') {
      return message;
    }
  }
  return undefined;
}

/**
 * OpenCode ends a step or tool cut short by an interrupt with a structured
 * `aborted` error ("Step interrupted", "Tool execution interrupted"). The
 * turn's Stopped status already reports it; real failures carry other types
 * and still render as errors.
 */
function isAbortedError(error: unknown): boolean {
  return typeof error === 'object' && error !== null && 'type' in error && error.type === 'aborted';
}

/**
 * OpenCode reports a declined permission as an aborted call with this reason,
 * and one the page declined with its note (`DECLINE_NOTE`), which the agent
 * reads and goes on from, as a `permission.rejected` failure whose reason is
 * that note. Any other rejection is a failure with its own reason.
 */
function isDeclinedError(error: unknown): boolean {
  if (typeof error !== 'object' || error === null || !('type' in error)) {
    return false;
  }
  const message = errorMessageOf(error);
  return error.type === 'aborted'
    ? message?.startsWith('The user declined') === true
    : error.type === 'permission.rejected' && message === DECLINE_NOTE;
}

/** OpenCode reports missing read/search paths differently; neither stops the agent exploring. */
function isNotFoundError(kind: TraceStepKind, error: unknown): boolean {
  const message =
    typeof error === 'object' && error !== null && 'message' in error ? error.message : error;
  if (typeof message !== 'string') {
    return false;
  }
  return (
    (kind === 'read' && message.startsWith('File not found:')) ||
    ((kind === 'grep' || kind === 'glob') && message.startsWith('Search path does not exist:'))
  );
}

type StepFailure = Pick<TraceStep, 'failed' | 'interrupted' | 'denied' | 'notFound' | 'error'>;

/**
 * How a failed call's step reads. A call cut short by an interrupt and a read/search of a missing
 * path read quietly; a subagent call whose child was cancelled reads as stopped too, and one
 * whose child failed shows the child's own error (`subagentFailure`).
 */
function failedStep(kind: TraceStepKind, error: unknown): StepFailure {
  // OpenCode aborts a call the person declined, or fails it with the note the decline carried;
  // the reason tells it from a Stop, and says no more than that to the person, so the row
  // carries the state alone.
  if (isDeclinedError(error)) {
    return { denied: true };
  }
  if (isAbortedError(error)) {
    return { interrupted: true };
  }
  if (kind === 'agent') {
    return subagentFailure(errorMessageOf(error));
  }
  if (isNotFoundError(kind, error)) {
    return { notFound: true, error: errorMessageOf(error) };
  }
  return { failed: true, error: errorMessageOf(error) };
}

function isQuestionTool(content: Extract<AssistantContent, { type: 'tool' }>): boolean {
  return content.name === QUESTION_TOOL_NAME;
}

/**
 * A bot run's result or batch call: the turn's result card shows what it handed back,
 * and session findings show batch verdicts instead of meaningless trace steps.
 */
function isResultTool(content: Extract<AssistantContent, { type: 'tool' }>): boolean {
  return content.name === SUBMIT_RESULT_TOOL_NAME || content.name === SUBMIT_BATCH_TOOL_NAME;
}

function isPendingQuestionTool(content: Extract<AssistantContent, { type: 'tool' }>): boolean {
  return (
    isQuestionTool(content) &&
    (content.state.status === 'running' || content.state.status === 'streaming')
  );
}

/** A tool call after which the session's branch can have a pull request. */
function publishesBranch(toolName: string, input: unknown): boolean {
  return toolName === OPEN_PULL_REQUEST_TOOL || isPushCommand(toolName, input);
}

/** Tools the transcript shows as their own card rather than as a trace step. */
function isCardTool(content: Extract<AssistantContent, { type: 'tool' }>): boolean {
  return (
    isQuestionTool(content) || isCallCardTool(content) || savedFindingsPart(content) !== undefined
  );
}

/** Tools whose card the call's input makes, and which an errored call leaves out. */
function isCallCardTool(content: Extract<AssistantContent, { type: 'tool' }>): boolean {
  return callCardOf(content.name) !== undefined;
}

function formEventId(data: Record<string, unknown>): string | undefined {
  const id = data.id ?? data.formID;
  return typeof id === 'string' && id.length > 0 ? id : undefined;
}

const FORM_RESOLVED_EVENTS = new Set(['form.replied', 'form.cancelled']);

export function formWasSeen(
  forms: readonly FormInfo[] | undefined,
  events: readonly { type: string }[],
  answers?: Record<string, { status: string }>,
): boolean {
  if ((forms?.length ?? 0) > 0) {
    return true;
  }
  if (
    events.some((event) => event.type === 'form.created' || FORM_RESOLVED_EVENTS.has(event.type))
  ) {
    return true;
  }
  return (
    answers !== undefined &&
    Object.values(answers).some(
      (answer) =>
        answer.status === 'submitted' ||
        answer.status === 'submitting' ||
        answer.status === 'failed',
    )
  );
}

function optionEntries(options: unknown): SessionQuestionOption[] {
  if (!Array.isArray(options)) {
    return [];
  }
  return options.flatMap((option) => {
    if (typeof option === 'string' && option.length > 0) {
      return [{ value: option, label: option }];
    }
    if (typeof option === 'object' && option !== null) {
      const record = option as { value?: unknown; label?: unknown; description?: unknown };
      const label =
        typeof record.label === 'string' && record.label.length > 0
          ? record.label
          : typeof record.value === 'string'
            ? record.value
            : '';
      const value =
        typeof record.value === 'string' && record.value.length > 0 ? record.value : label;
      if (label.length === 0) {
        return [];
      }
      return typeof record.description === 'string' && record.description.length > 0
        ? [{ value, label, description: record.description }]
        : [{ value, label }];
    }
    return [];
  });
}

function questionFieldFromOptions(
  key: string,
  prompt: string,
  options: unknown,
  extra: Partial<Pick<SessionQuestionField, 'header' | 'multiple' | 'required' | 'custom'>> = {},
): SessionQuestionField {
  const optionValues = optionEntries(options);
  return {
    key,
    prompt,
    options: optionValues.map((option) => option.label),
    ...(optionValues.length > 0 ? { optionValues } : {}),
    ...(optionValues.length === 0 ? { freeText: true } : {}),
    ...extra,
  };
}

/** A question's short header for the dock's title, when it has one besides its prompt. */
function questionHeader(header: unknown, prompt: string): Pick<SessionQuestionField, 'header'> {
  const text = typeof header === 'string' ? header.trim() : '';
  return text && text !== prompt.trim() ? { header: text } : {};
}

function formFieldToQuestion(field: FormField): SessionQuestionField {
  // The question tool puts its short header in the title and the question
  // itself in the description.
  const prompt =
    [field.description, field.title].find((text) => text !== undefined && text.trim().length > 0) ??
    field.key;
  const header = questionHeader(field.title, prompt);
  switch (field.type) {
    case 'string':
      return questionFieldFromOptions(field.key, prompt, field.options, {
        ...header,
        ...(field.required === false ? { required: false } : {}),
        ...(field.custom === true ? { custom: true } : {}),
      });
    case 'multiselect':
      return questionFieldFromOptions(field.key, prompt, field.options, {
        ...header,
        multiple: true,
        ...(field.required === false ? { required: false } : {}),
        ...(field.custom === true ? { custom: true } : {}),
      });
    case 'number':
    case 'integer':
    case 'boolean':
      return questionFieldFromOptions(field.key, prompt, undefined, {
        ...header,
        ...(field.required === false ? { required: false } : {}),
      });
    case 'external':
      return questionFieldFromOptions(field.key, prompt, undefined, header);
    default: {
      const _exhaustive: never = field;
      return _exhaustive;
    }
  }
}

function questionToolFields(input: unknown): SessionQuestionField[] {
  const record =
    typeof input === 'object' && input !== null ? (input as Record<string, unknown>) : null;
  if (record !== null && Array.isArray(record.questions)) {
    return record.questions.flatMap((question, index) => {
      if (typeof question !== 'object' || question === null) {
        return [];
      }
      const record = question as {
        id?: unknown;
        question?: unknown;
        header?: unknown;
        options?: unknown;
        multiple?: unknown;
        custom?: unknown;
      };
      const prompt =
        typeof record.question === 'string' && record.question.length > 0
          ? record.question
          : typeof record.header === 'string'
            ? record.header
            : '';
      const key =
        typeof record.id === 'string' && record.id.length > 0 ? record.id : `question_${index}`;
      return [
        questionFieldFromOptions(key, prompt, record.options, {
          ...questionHeader(record.header, prompt),
          ...(record.multiple === true ? { multiple: true } : {}),
          ...(record.custom === true ? { custom: true } : {}),
        }),
      ];
    });
  }
  const prompt =
    typeof input === 'string' ? input : stringInput(input, ['question', 'prompt', 'title', 'text']);
  const options =
    typeof input === 'object' && input !== null
      ? (input as { options?: unknown }).options
      : undefined;
  return [questionFieldFromOptions('question_0', prompt, options)];
}

function questionPartFromFields(
  fields: SessionQuestionField[],
  prompt: string,
  formId?: string,
): SessionPart {
  return {
    type: 'question',
    prompt,
    options: fields[0]?.options ?? [],
    fields,
    ...(formId === undefined ? {} : { formId }),
  };
}

function reasoningDurationSeconds(content: Extract<AssistantContent, { type: 'reasoning' }>): {
  durationSeconds: number;
  running: boolean;
} {
  const created = content.time?.created;
  const completed = content.time?.completed;
  if (created === undefined) {
    return { durationSeconds: 0, running: false };
  }
  if (completed === undefined) {
    return { durationSeconds: 0, running: true };
  }
  return { durationSeconds: Math.max(0, Math.round((completed - created) / 1000)), running: false };
}

function assistantMessageCompleted(message: SessionMessageAssistant): boolean {
  return message.time.completed !== undefined;
}

function endedStreamKey(
  partType: 'text' | 'reasoning',
  data: Record<string, unknown>,
  ctx: FoldContext,
): string | undefined {
  const keyed = streamKeyOf(partType, data);
  if (keyed !== undefined || typeof data.assistantMessageID !== 'string') {
    return keyed;
  }
  const prefix = `${data.assistantMessageID}:${partType}:`;
  const active = [...new Set([...ctx.deltaParts.keys(), ...ctx.streamStartedAt.keys()])].filter(
    (key) => key.startsWith(prefix) && !ctx.endedStreams.has(key),
  );
  return active.at(-1) ?? `${prefix}0`;
}

function toolStreamKey(data: Record<string, unknown>): string | undefined {
  return typeof data.assistantMessageID === 'string' && typeof data.id === 'string'
    ? `${data.assistantMessageID}:tool:${data.id}`
    : undefined;
}

/**
 * Keys of text/reasoning/tool content already persisted on assistant messages.
 * An unfinished message saves each text and reasoning part empty until the part
 * ends, so a part saved empty is not persisted yet: its live deltas fill it. A
 * tool step saved before its call finished is not persisted either: its live
 * events update it, and they name its input and its end.
 */
function persistedStreamKeys(messages: readonly SessionMessageInfo[]): Set<string> {
  const keys = new Set<string>();
  for (const message of messages) {
    if (message.type !== 'assistant') {
      continue;
    }
    const completed = assistantMessageCompleted(message);
    let textOrdinal = 0;
    let reasoningOrdinal = 0;
    for (const content of message.content) {
      switch (content.type) {
        case 'text':
          if (completed || content.text.length > 0) {
            keys.add(`${message.id}:text:${textOrdinal}`);
          }
          textOrdinal += 1;
          break;
        case 'reasoning':
          if (completed || content.text.length > 0) {
            keys.add(`${message.id}:reasoning:${reasoningOrdinal}`);
          }
          reasoningOrdinal += 1;
          break;
        case 'tool':
          if (
            completed ||
            isCardTool(content) ||
            content.state.status === 'completed' ||
            content.state.status === 'error'
          ) {
            keys.add(`${message.id}:tool:${content.id}`);
          }
          break;
        default: {
          const _exhaustive: never = content;
          return _exhaustive;
        }
      }
    }
  }
  return keys;
}

type FoldContext = {
  persisted: Set<string>;
  retries: ReturnType<typeof responseRetryFold>;
  /** Delta-built parts keyed by `assistantMessageID:ordinal`. */
  deltaParts: Map<string, SessionPart>;
  endedStreams: Set<string>;
  /**
   * Tool calls keyed by `assistantMessageID:tool:<callId>` for live updates, and whether this page
   * saw each start (`session.tool.called`): a step saved while it ran started on the sandbox's
   * clock.
   */
  toolSteps: Map<string, { name: string; step: TraceStep; calledLive?: boolean }>;
  /** Each call retains its original trace even after later text starts a new group. */
  traceByTool: Map<string, SessionPart>;
  /** Environment suggestion and page cards keyed like tool steps, for live updates. */
  callCards: Map<string, CallCardPart>;
  /** Code Mode metadata keyed by call identity, independent of saved/live lifecycle ownership. */
  codeMode: Map<string, CodeModeScript>;
  /** `at` timestamp of each `*.started` event, for reasoning durations. */
  streamStartedAt: Map<string, number>;
  /** Running parts belong to an assistant message, never to a queued turn. */
  runningByMessage: Map<string, Set<SessionPart>>;
  stepMessages: Set<string>;
  executionMessages: Set<string>;
  messageParts: Map<string, SessionPart[]>;
  partIds: Map<SessionPart, string>;
  latestStepStartSeq?: number;
  latestExecutionStartSeq?: number;
  /** Error messages already surfaced — turn/session_status failures dedupe. */
  errors: Map<string, Extract<SessionPart, { type: 'error' }>>;
  /** Each scope's subscription card, which its later errors fold into (`pushError`). */
  subscriptionErrors: Map<string, Extract<SessionPart, { type: 'error' }>>;
  /** The subscription the session runs on (`BuildTimelineInput`). */
  aiSubscriptionProvider: AiSubscriptionProvider | undefined;
  errorScope?: string;
  lastErrorScope?: string;
  /** Form ids already rendered — snapshot forms and form.created dedupe. */
  forms: Set<string>;
  /** OpenCode splices store.question on reply/reject; we drop these form cards. */
  resolvedForms: Set<string>;
  /** What the turn relayed of each subagent, which the final pass gives the calls that run. */
  subagentActivity: SubagentActivities;
};

/** Parts shown as running until their message's step or execution settles. */
const RUNNING_PART_TYPES = ['reasoning', 'trace', 'environment_suggestion', 'page'] as const;

type RunningPart = Extract<SessionPart, { type: (typeof RUNNING_PART_TYPES)[number] }>;

function hasRunningState(part: SessionPart | undefined): part is RunningPart {
  return (RUNNING_PART_TYPES as readonly string[]).includes(part?.type ?? '');
}

function registerMessagePart(
  ctx: FoldContext,
  messageId: string,
  part: SessionPart,
  id?: string,
): void {
  const messageParts = ctx.messageParts.get(messageId) ?? [];
  if (!messageParts.includes(part)) {
    messageParts.push(part);
  }
  ctx.messageParts.set(messageId, messageParts);
  if (id !== undefined && !ctx.partIds.has(part)) {
    ctx.partIds.set(part, id);
    if (part.type === 'reasoning') {
      part.id = id;
      part.messageId = messageId;
    }
  }
}

function trackRunning(ctx: FoldContext, messageId: string, part: SessionPart): void {
  const running = ctx.runningByMessage.get(messageId) ?? new Set<SessionPart>();
  running.add(part);
  ctx.runningByMessage.set(messageId, running);
  trackMessage(ctx, messageId);
}

function trackMessage(ctx: FoldContext, messageId: string): void {
  ctx.stepMessages.add(messageId);
  ctx.executionMessages.add(messageId);
}

function settleMessages(
  ctx: FoldContext,
  messageIds: Iterable<string>,
  options: { interrupted?: boolean } = {},
): void {
  for (const messageId of messageIds) {
    for (const part of ctx.runningByMessage.get(messageId) ?? []) {
      if (hasRunningState(part)) {
        part.running = undefined;
      }
      if (part.type === 'trace') {
        for (const step of part.steps) {
          // A call an interrupt ended before its own end event reached the chat was cut short.
          if (options.interrupted && step.running) {
            step.interrupted = true;
          }
          step.running = undefined;
        }
      }
    }
    ctx.runningByMessage.delete(messageId);
  }
}

/**
 * The card names its scope's failure once. A subscription's says why, and what to do about it,
 * which a generic one folded in first, such as the turn's own end, does not. Once a scope has a
 * subscription's card, that card stands for the scope's failure.
 */
function adoptAiSubscriptionError(
  ctx: FoldContext,
  card: Extract<SessionPart, { type: 'error' }>,
  error: TranscriptError,
): void {
  if (error.aiSubscription !== undefined && card.aiSubscription === undefined) {
    card.message = error.message;
    card.aiSubscription = error.aiSubscription;
  }
  if (card.aiSubscription !== undefined && ctx.errorScope !== undefined) {
    ctx.subscriptionErrors.set(ctx.errorScope, card);
  }
}

function pushError(
  parts: SessionPart[],
  ctx: FoldContext,
  error: TranscriptError,
): Extract<SessionPart, { type: 'error' }> {
  const key = JSON.stringify([ctx.errorScope, error.message]);
  const last = parts.at(-1);
  // A later error of a scope with a subscription's card folds into it even when other parts came
  // between, as the files a failed turn shared come before its own end: a second, generic card
  // would offer the Retry, or the owner's words, the subscription's card withholds.
  const card =
    ctx.errors.get(key) ??
    (last?.type === 'error' && ctx.lastErrorScope === ctx.errorScope ? last : undefined) ??
    (ctx.errorScope === undefined ? undefined : ctx.subscriptionErrors.get(ctx.errorScope));
  if (card) {
    ctx.errors.set(key, card);
    adoptAiSubscriptionError(ctx, card, error);
    return card;
  }
  const part: Extract<SessionPart, { type: 'error' }> = { type: 'error', ...error };
  ctx.errors.set(key, part);
  ctx.lastErrorScope = ctx.errorScope;
  adoptAiSubscriptionError(ctx, part, error);
  parts.push(part);
  return part;
}

function pushResponseRetry(
  parts: SessionPart[],
  ctx: FoldContext,
  messageId: string,
  error: unknown,
  failure?: { at: number; opencodeSeq?: number },
): boolean {
  const part = ctx.retries.notice(messageId, error, ctx.errorScope, failure);
  if (part === undefined) {
    return false;
  }
  if (!ctx.partIds.has(part)) {
    parts.push(part);
    registerMessagePart(ctx, messageId, part, part.id);
  }
  return true;
}

/** Append a streamed text/reasoning delta to the matching part (or start one). */
function applyDelta(
  parts: SessionPart[],
  ctx: FoldContext,
  partType: 'text' | 'reasoning',
  data: Record<string, unknown>,
  at: number,
): void {
  const delta =
    typeof data.delta === 'string' ? data.delta : typeof data.text === 'string' ? data.text : '';
  if (delta.length === 0) {
    return;
  }
  const key = streamKeyOf(partType, data);
  if (key !== undefined) {
    if (ctx.persisted.has(key)) {
      if (partType === 'reasoning' && typeof data.assistantMessageID === 'string') {
        trackMessage(ctx, data.assistantMessageID);
      }
      return;
    }
    if (ctx.endedStreams.has(key)) {
      return;
    }
    const existing = ctx.deltaParts.get(key);
    if (existing !== undefined) {
      if (existing.type === 'text' && partType === 'text') {
        existing.markdown += delta;
      } else if (existing.type === 'reasoning' && partType === 'reasoning') {
        existing.text += delta;
      }
      return;
    }
  }
  // Unkeyed deltas (older wire shape) fall back to the last matching part;
  // keyed deltas for a new stream always start their own part.
  if (key === undefined) {
    for (let index = parts.length - 1; index >= 0; index -= 1) {
      const part = parts[index];
      if (part === undefined || part.type === 'user' || part.type === 'error') {
        break;
      }
      if (partType === 'text' && part.type === 'text') {
        part.markdown += delta;
        return;
      }
      if (partType === 'reasoning' && part.type === 'reasoning') {
        part.text += delta;
        return;
      }
    }
  }
  const part: SessionPart =
    partType === 'text'
      ? {
          type: 'text',
          markdown: delta,
          createdAt: (key === undefined ? undefined : ctx.streamStartedAt.get(key)) ?? at,
        }
      : { type: 'reasoning', text: delta, durationSeconds: 0, running: true };
  parts.push(part);
  if (key !== undefined) {
    ctx.deltaParts.set(key, part);
    if (typeof data.assistantMessageID === 'string') {
      registerMessagePart(ctx, data.assistantMessageID, part, key);
    }
    if (part.type === 'reasoning' && typeof data.assistantMessageID === 'string') {
      trackRunning(ctx, data.assistantMessageID, part);
    }
  }
}

/**
 * OpenCode groups only consecutive tools (message-part.tsx `groupParts`).
 * A text/reasoning/question boundary starts a new trace, even in one message.
 * Call identity separately pins late lifecycle updates to the original trace.
 */
function traceFor(
  parts: SessionPart[],
  ctx: FoldContext,
  messageId: string,
  key: string,
): SessionPart {
  const last = parts.at(-1);
  if (last?.type === 'trace' && ctx.messageParts.get(messageId)?.includes(last)) {
    ctx.traceByTool.set(key, last);
    return last;
  }
  const part: SessionPart = { type: 'trace', steps: [] };
  parts.push(part);
  ctx.traceByTool.set(key, part);
  registerMessagePart(ctx, messageId, part, key);
  return part;
}

type CallCardPart = EnvironmentSuggestionPart | AgentPagePart;

/**
 * How a card tool's call makes its card, live and saved. Later live events go
 * through `foldCallCardEvent`, whose switch narrows each card to its own part type.
 */
type CallCard = {
  live: (callId: string) => CallCardPart;
  saved: (
    content: Extract<AssistantContent, { type: 'tool' }>,
    completed: boolean,
  ) => CallCardPart | undefined;
};

const CALL_CARDS: Readonly<Record<string, CallCard>> = {
  [ENVIRONMENT_SUGGESTION_TOOL]: {
    live: liveEnvironmentSuggestionPart,
    saved: savedEnvironmentSuggestionPart,
  },
  [SHOW_PAGE_TOOL]: { live: liveAgentPagePart, saved: savedAgentPagePart },
};

function callCardOf(name: string): CallCard | undefined {
  return Object.hasOwn(CALL_CARDS, name) ? CALL_CARDS[name] : undefined;
}

function foldCallCardEvent(
  parts: SessionPart[],
  card: CallCardPart,
  type: string,
  data: Record<string, unknown>,
): void {
  switch (card.type) {
    case 'environment_suggestion':
      foldEnvironmentSuggestionEvent(parts, card, type);
      return;
    case 'page':
      foldAgentPageEvent(parts, card, type, data);
      return;
    default: {
      const _exhaustive: never = card;
      return _exhaustive;
    }
  }
}

function foldToolEvent(parts: SessionPart[], ctx: FoldContext, event: CodeSessionAgentEvent): void {
  const data = eventData(event);
  const key = toolStreamKey(data);
  if (key !== undefined && ctx.retries.unexecutedTools.has(key)) {
    return;
  }
  recordCodeModeScript(ctx.codeMode, key, data);
  if (key !== undefined && ctx.persisted.has(key)) {
    trackMessage(ctx, data.assistantMessageID as string);
    return;
  }
  if (key === undefined) {
    return;
  }
  // Both ids are strings once `toolStreamKey` made a key of them.
  const messageId = data.assistantMessageID as string;
  const callId = data.id as string;

  if (event.type === 'session.tool.input.started') {
    if (ctx.toolSteps.has(key) || ctx.callCards.has(key)) {
      return;
    }
    const name = typeof data.name === 'string' ? data.name : '';
    const card = callCardOf(name);
    if (card !== undefined) {
      // Its own card, like a question: not a step, and later tools start a new trace.
      const liveCard = card.live(callId);
      parts.push(liveCard);
      ctx.callCards.set(key, liveCard);
      registerMessagePart(ctx, messageId, liveCard, key);
      trackRunning(ctx, messageId, liveCard);
      return;
    }
    if (STEPLESS_LIVE_TOOLS.has(name)) {
      // Not a step: the turn's result card shows what a run handed back, and a findings
      // report's saved message shows it as its card.
      return;
    }
    const kind = toolStepKind(name);
    const step: TraceStep = {
      kind,
      label: toolStepLabel(name),
      detail: '',
      running: true,
      command: COMMAND_TOOL_NAMES.has(name) ? {} : undefined,
      subagent: kind === 'agent' ? { callId } : undefined,
      call: livePageToolCall(name, data),
      callId,
    };
    ctx.toolSteps.set(key, { name, step });
    const trace = traceFor(parts, ctx, messageId, key);
    if (trace.type === 'trace') {
      trace.steps.push(step);
      trace.running = true;
      trackRunning(ctx, messageId, trace);
    }
    return;
  }

  const card = ctx.callCards.get(key);
  if (card !== undefined) {
    foldCallCardEvent(parts, card, event.type, data);
    return;
  }
  const tool = ctx.toolSteps.get(key);
  if (tool === undefined) {
    return;
  }
  const { name, step } = tool;
  if (step.subagent !== undefined) {
    foldSubagentEvent(step.subagent, event);
  }
  switch (event.type) {
    case 'session.tool.input.delta': {
      // `text` is the streamed input so far (JSON once complete); parse for a
      // detail string when it decodes, ignore partial JSON otherwise.
      if (step.detail === '' && typeof data.text === 'string') {
        try {
          Object.assign(step, toolStepDetail(name, JSON.parse(data.text)));
        } catch {
          // Partial JSON mid-stream — wait for `tool.input.ended`/`called`.
        }
      }
      return;
    }
    case 'session.tool.input.ended': {
      // `text` is the JSON-encoded input; prefer the structured `input` from
      // `tool.called`, fall back to parsing this for the detail string. A Code
      // Mode call has its own script disclosure and recorded children, so
      // never show the JSON as the detail.
      if (
        step.detail === '' &&
        name !== OPENCODE_CODE_MODE_TOOL_NAME &&
        typeof data.text === 'string'
      ) {
        try {
          Object.assign(step, toolStepDetail(name, JSON.parse(data.text)));
        } catch {
          step.detail = data.text;
        }
      }
      return;
    }
    case 'session.tool.called': {
      const detail = toolStepDetail(name, data.input);
      if (detail.detail !== '') {
        Object.assign(step, detail);
      }
      recordPageToolCall(step, { input: data.input });
      step.startedAt = event.at;
      tool.calledLive = true;
      return;
    }
    // `session.tool.progress` updates the children, which the metadata fold
    // above the persisted/lifecycle checks has already read.
    case 'session.tool.success': {
      step.running = undefined;
      step.endedAt = event.at;
      const codeModeError = failedCodeModeError(name, data.content, data.metadata);
      if (codeModeError !== undefined) {
        step.failed = true;
        step.error = codeModeError;
      } else if (step.command === undefined) {
        step.output = finishedStepOutput(step.kind, data.content) ?? step.output;
      } else {
        const { output, ...status } = shellCommandOutput(data.content, data.metadata);
        step.output = output ?? step.output;
        // Timed by cubic's clock alone: a call this page never saw start is timed by its saved
        // message once it lands.
        step.command = { ...status, ...(tool.calledLive === true ? commandDuration(step) : {}) };
      }
      step.diffs = toolStepDiffs(data.metadata);
      recordPageToolCall(step, { output: toolContentText(data.content) });
      const trace = ctx.traceByTool.get(key);
      if (trace?.type === 'trace') {
        trace.running = trace.steps.some((entry) => entry.running === true) ? true : undefined;
      }
      return;
    }
    case 'session.tool.failed': {
      step.running = undefined;
      step.endedAt = event.at;
      Object.assign(step, failedStep(step.kind, data.error));
      if (step.failed === true) {
        step.error ??= 'Failed';
      }
      const trace = ctx.traceByTool.get(key);
      if (trace?.type === 'trace') {
        trace.running = trace.steps.some((entry) => entry.running === true) ? true : undefined;
      }
    }
  }
}

/** A pull request page's tool call as its live step starts it; undefined for any other tool. */
function livePageToolCall(name: string, data: Record<string, unknown>): TraceStepCall | undefined {
  return PR_PAGE_TOOL_NAMES.has(name) && typeof data.id === 'string'
    ? { id: data.id, name }
    : undefined;
}

/** What a live event says of a pull request page's tool call, kept on its step (`TraceStep.call`). */
function recordPageToolCall(step: TraceStep, update: Partial<TraceStepCall>): void {
  if (step.call !== undefined) {
    Object.assign(step.call, update);
  }
}

function formQuestionPart(form: FormInfo): SessionPart {
  const permission = permissionFormDetails(form);
  if (permission !== null) {
    return {
      type: 'question',
      prompt: permission.command ?? permission.resources.join('\n'),
      options: [],
      fields: [],
      formId: form.id,
      permission,
    };
  }
  const fields = form.fields.map(formFieldToQuestion);
  const prompt = fields.length === 1 ? (fields[0]?.prompt ?? form.title) : form.title;
  return questionPartFromFields(fields, prompt, form.id);
}

// eslint-disable-next-line complexity
function foldAgentEvent(
  parts: SessionPart[],
  event: CodeSessionAgentEvent,
  ctx: FoldContext,
): void {
  const data = eventData(event);
  const staleStepTerminal =
    event.opencodeSeq !== undefined &&
    ctx.latestStepStartSeq !== undefined &&
    event.opencodeSeq < ctx.latestStepStartSeq;
  const staleExecutionTerminal =
    event.opencodeSeq !== undefined &&
    ctx.latestExecutionStartSeq !== undefined &&
    event.opencodeSeq < ctx.latestExecutionStartSeq;
  switch (event.type) {
    case 'session.text.delta':
      applyDelta(parts, ctx, 'text', data, event.at);
      return;
    case 'session.reasoning.delta':
      applyDelta(parts, ctx, 'reasoning', data, event.at);
      return;
    case 'session.text.started':
    case 'session.reasoning.started': {
      const partType = event.type === 'session.text.started' ? 'text' : 'reasoning';
      const key = streamKeyOf(partType, data);
      if (key !== undefined) {
        ctx.streamStartedAt.set(key, event.at);
        if (ctx.persisted.has(key) && typeof data.assistantMessageID === 'string') {
          trackMessage(ctx, data.assistantMessageID);
        }
        if (partType === 'reasoning' && !ctx.persisted.has(key) && !ctx.deltaParts.has(key)) {
          const part: SessionPart = {
            type: 'reasoning',
            text: '',
            durationSeconds: 0,
            running: true,
          };
          parts.push(part);
          ctx.deltaParts.set(key, part);
          if (typeof data.assistantMessageID === 'string') {
            registerMessagePart(ctx, data.assistantMessageID, part, key);
          }
          if (typeof data.assistantMessageID === 'string') {
            trackRunning(ctx, data.assistantMessageID, part);
          }
        }
      }
      return;
    }
    case 'session.text.ended':
    case 'session.reasoning.ended': {
      const partType = event.type === 'session.text.ended' ? 'text' : 'reasoning';
      const key = endedStreamKey(partType, data, ctx);
      let part = key === undefined ? undefined : ctx.deltaParts.get(key);
      if (
        part === undefined &&
        key !== undefined &&
        !ctx.persisted.has(key) &&
        typeof data.text === 'string' &&
        data.text.trim().length > 0
      ) {
        part =
          partType === 'text'
            ? {
                type: 'text',
                markdown: data.text,
                createdAt: ctx.streamStartedAt.get(key) ?? event.at,
              }
            : { type: 'reasoning', text: data.text, durationSeconds: 0 };
        parts.push(part);
        ctx.deltaParts.set(key, part);
        if (typeof data.assistantMessageID === 'string') {
          registerMessagePart(ctx, data.assistantMessageID, part, key);
        }
      }
      if (key !== undefined && part !== undefined) {
        ctx.endedStreams.add(key);
      }
      if (part === undefined) {
        return;
      }
      if (part.type === 'reasoning') {
        part.running = undefined;
        if (typeof data.text === 'string') {
          part.text = data.text;
        }
        const startedAt = key === undefined ? undefined : ctx.streamStartedAt.get(key);
        if (startedAt !== undefined) {
          part.durationSeconds = Math.max(0, Math.round((event.at - startedAt) / 1000));
        }
        return;
      }
      // `text.ended` carries the authoritative full text — prefer it over the
      // accumulated deltas so a dropped delta can't corrupt the transcript.
      if (part.type === 'text' && typeof data.text === 'string' && data.text.length > 0) {
        part.markdown = data.text;
      }
      return;
    }
    case 'session.step.failed':
    case 'session.execution.failed': {
      if (event.type === 'session.step.failed' ? staleStepTerminal : staleExecutionTerminal) {
        return;
      }
      settleMessages(
        ctx,
        event.type === 'session.step.failed' ? ctx.stepMessages : ctx.executionMessages,
      );
      if (isAbortedError(data.error)) {
        ctx.retries.settle('stopped', ctx.errorScope, event.opencodeSeq);
        return;
      }
      if (
        event.type === 'session.step.failed' &&
        typeof data.assistantMessageID === 'string' &&
        pushResponseRetry(parts, ctx, data.assistantMessageID, data.error, {
          at: event.at,
          opencodeSeq: event.opencodeSeq,
        })
      ) {
        return;
      }
      const error = transcriptErrorOf(data.error, ctx) ?? {
        message: typeof data.message === 'string' ? data.message : 'The turn failed',
      };
      ctx.retries.settle('stopped', ctx.errorScope, event.opencodeSeq);
      pushError(parts, ctx, error);
      return;
    }
    case 'session.retry.scheduled':
      if (typeof data.assistantMessageID === 'string') {
        pushResponseRetry(parts, ctx, data.assistantMessageID, data.error);
      }
      return;
    case 'session.inbox.delivered':
      ctx.retries.settle('stopped', undefined, event.opencodeSeq);
      return;
    case 'session.step.started':
      if (staleStepTerminal) {
        return;
      }
      ctx.stepMessages = new Set();
      ctx.latestStepStartSeq = event.opencodeSeq;
      return;
    case 'session.execution.started':
      if (staleExecutionTerminal) {
        return;
      }
      ctx.executionMessages = new Set();
      ctx.latestExecutionStartSeq = event.opencodeSeq;
      return;
    case 'session.step.ended':
      if (!staleStepTerminal) {
        settleMessages(ctx, ctx.stepMessages);
        if (successfulResponseFinish(data.finish)) {
          ctx.retries.settle('recovered', ctx.errorScope, event.opencodeSeq);
        }
      }
      return;
    case 'session.execution.succeeded':
    case 'session.execution.interrupted':
      if (!staleExecutionTerminal) {
        settleMessages(ctx, ctx.executionMessages, {
          interrupted: event.type === 'session.execution.interrupted',
        });
        if (event.type === 'session.execution.succeeded' || data.reason !== 'shutdown') {
          ctx.retries.settle(
            event.type === 'session.execution.succeeded' ? 'recovered' : 'stopped',
            undefined,
            event.opencodeSeq,
          );
        }
      }
      return;
    case 'form.created': {
      const form = data.form;
      if (typeof form === 'object' && form !== null) {
        const parsed = form as FormInfo;
        if (
          typeof parsed.id === 'string' &&
          Array.isArray(parsed.fields) &&
          !ctx.forms.has(parsed.id) &&
          !ctx.resolvedForms.has(parsed.id)
        ) {
          ctx.forms.add(parsed.id);
          parts.push(formQuestionPart(parsed));
        }
      }
      return;
    }
    case 'form.replied':
    case 'form.cancelled': {
      const id = formEventId(data);
      if (id !== undefined) {
        ctx.resolvedForms.add(id);
      }
      return;
    }
    case SUBAGENT_ACTIVITY_EVENT:
      noteSubagentActivity(ctx.subagentActivity, event.data);
      return;
    default:
      if (event.type.startsWith('session.tool.')) {
        foldToolEvent(parts, ctx, event);
      }
  }
}

function foldAssistantContent(
  parts: SessionPart[],
  content: AssistantContent,
  completed: boolean,
  appendToTrace: boolean,
  /** OpenCode closed the message when the next prompt started, after a kill: its times say nothing. */
  repaired = false,
): SessionPart | undefined {
  if (content.type === 'text') {
    const part: SessionPart = { type: 'text', markdown: content.text };
    parts.push(part);
    return part;
  }
  if (content.type === 'reasoning') {
    const { durationSeconds, running } = reasoningDurationSeconds(content);
    if (content.text.trim().length === 0 && (completed || !running)) {
      return;
    }
    const part: SessionPart = {
      type: 'reasoning',
      text: content.text,
      durationSeconds,
      running: running && !completed ? true : undefined,
    };
    parts.push(part);
    return part;
  }
  const card = callCardOf(content.name);
  if (card !== undefined) {
    const part = card.saved(content, completed);
    if (part !== undefined) {
      parts.push(part);
    }
    return part;
  }
  if (isQuestionTool(content)) {
    // Completed/errored question tools are not pending. OpenCode removes the
    // request from store.question on reply; remounting this card looks like 1/N.
    if (!isPendingQuestionTool(content)) {
      return;
    }
    const input = 'input' in content.state ? content.state.input : undefined;
    const fields = questionToolFields(input);
    const part = questionPartFromFields(fields, fields[0]?.prompt ?? '');
    parts.push(part);
    return part;
  }
  if (isResultTool(content)) {
    return;
  }
  // A findings report shows nothing while cubic checks it, as its live events do. Once
  // checked, one with something to act on is its own card; any other reads as a step.
  if (isUncheckedFindingsReport(content)) {
    return;
  }
  const findings = savedFindingsPart(content);
  if (findings !== undefined) {
    parts.push(findings);
    return findings;
  }
  const step = toolTraceStep(content);
  if (completed) {
    step.running = undefined;
  }
  // A kill stores nothing about the stop: the repair closes the cut-off step with the reply, when
  // the next prompt starts, so the step has no end to show. A stop closes it itself, at the stop.
  if (repaired && step.interrupted) {
    step.endedAt = undefined;
    if (step.command !== undefined) {
      delete step.command.durationMs;
    }
  }
  const last = parts[parts.length - 1];
  if (appendToTrace && last?.type === 'trace') {
    last.steps.push(step);
    last.running = last.running === true || step.running === true ? true : undefined;
    return last;
  }
  const part: SessionPart = {
    type: 'trace',
    steps: [step],
    running: step.running,
  };
  parts.push(part);
  return part;
}

function messageCreatedAt(message: SessionMessageInfo): number {
  return message.time.created;
}

const NO_HIDDEN_TURNS: ReadonlySet<string> = new Set();

/** Collect each turn's nonempty list, replacing rows with later events in order. */
function listsByTurn<T>(
  turns: readonly TimelineTurn[],
  turnEvents: readonly TurnEvent[],
  fromTurn: (turn: TimelineTurn) => readonly T[] | null | undefined,
  fromEvent: (event: TurnEvent) => readonly T[] | null | undefined,
): Map<string, readonly T[]> {
  const lists = new Map<string, readonly T[]>();
  for (const turn of turns) {
    const list = fromTurn(turn);
    if (list?.length) {
      lists.set(turn.id, list);
    }
  }
  for (const event of turnEvents) {
    const list = fromEvent(event);
    if (list?.length) {
      lists.set(event.turnId, list);
    }
  }
  return lists;
}

/**
 * Where each boot's rows sit once a phase started: first, or where cubic began a sandbox that
 * replaced an earlier one (`at`), whose log counts from its start again.
 */
function bootRowAnchors(
  { boots = [] }: Pick<BuildTimelineInput, 'boots'>,
  parts: SessionPart[],
  ctx: FoldContext,
): { at: number; run: () => void }[] {
  return boots.flatMap(({ phases: boot, at: bootAt = Number.NEGATIVE_INFINITY }, index) => {
    if (!boot.some((phase) => phase.status !== 'pending')) {
      return [];
    }
    // Only the current sandbox's card takes work.
    const replaced = index < boots.length - 1 || undefined;
    return [
      {
        at: bootAt,
        run: () => {
          ctx.latestStepStartSeq = undefined;
          ctx.latestExecutionStartSeq = undefined;
          parts.push({ type: 'boot', id: `boot:${index}`, phases: boot, replaced });
        },
      },
    ];
  });
}

/**
 * Where a turn's bubble sits until OpenCode delivers its prompt: when it was
 * sent, except a follow-up cubic claimed for its send. cubic claims one once
 * the work it waited for ended, so it then shows after that work.
 */
function undeliveredBubbleAt(turn: TimelineTurn): number {
  return turn.delivery === 'queue' && turn.status !== 'queued'
    ? (turn.claimedAt ?? turn.startedAt ?? turn.createdAt)
    : turn.createdAt;
}

/**
 * One ordered timeline for the whole session: each sandbox's boot rows (the first sandbox's
 * first, each later one's where cubic began it) → per-turn [user bubble → assistant parts] → queued/pending user
 * bubbles. Persisted user messages are deduped against turn rows via `inboxId`/`msg_<turnId>`;
 * event-built parts are deduped against persisted assistant content via
 * stream keys, so a persisted message replaces streamed parts for the same
 * key in the same position.
 */
// eslint-disable-next-line complexity
export function buildTimeline(input: BuildTimelineInput): SessionPart[] {
  const parts: SessionPart[] = [];
  // Saved turns only: a prompt sent while a rewind waits commits it first.
  const hidden = input.hidden ?? NO_HIDDEN_TURNS;

  // Merge saved content with local prompts by identity, as in T3/OpenCode.
  // Acknowledgments change status, but only saved content replaces local text.
  const promptCreatedAt = new Map(Object.entries(input.promptCreatedAt ?? {}));
  for (const pending of input.pendingPrompts ?? []) {
    promptCreatedAt.set(pending.turnId ?? pending.clientId, pending.createdAt);
  }
  const messageCreatedAtOverrides = new Map(
    [...promptCreatedAt].map(([turnId, at]) => [deterministicPromptId(turnId), at]),
  );
  const savedUserText = new Map(
    input.messages
      .filter((message) => message.type === 'user' && userMessageText(message).length > 0)
      .map((message) => [message.id, userMessageText(message)]),
  );
  const turns: TimelineTurn[] = (input.turns ?? []).map((turn) => ({
    ...turn,
    createdAt: promptCreatedAt.get(turn.id) ?? turn.createdAt,
  }));
  const knownTurnIds = new Set(turns.map((turn) => turn.id));
  for (const pending of input.pendingPrompts ?? []) {
    if (pending.turnId !== undefined && !knownTurnIds.has(pending.turnId)) {
      turns.push({
        id: pending.turnId,
        index: Number.MAX_SAFE_INTEGER,
        // Runtime content can arrive first, but only the saved turn carries authored text.
        prompt: savedUserText.get(deterministicPromptId(pending.turnId)) ?? pending.text,
        authoredText: pending.authoredText,
        attachments: pending.attachments,
        inboxId: deterministicPromptId(pending.turnId),
        status: 'queued',
        delivery: pending.delivery,
        createdAt: pending.createdAt,
        checkInReason: null,
        messageFrom: null,
        authorProfileId: input.promptAuthorId,
      });
      knownTurnIds.add(pending.turnId);
    }
  }
  for (const event of [...(input.turnEvents ?? [])].sort((a, b) => a.seq - b.seq)) {
    const turn = turns.find((candidate) => candidate.id === event.turnId);
    if (turn === undefined) {
      continue;
    }
    if (event.status === 'running') {
      turn.startedAt ??= event.at;
    } else if (event.status === 'admitted') {
      turn.claimedAt ??= event.at;
    }
    if (isTerminalTurnStatus(turn.status)) {
      continue;
    }
    // A snapshot may be newer than the replayed event. Admission cannot move
    // a running turn (or an admitted turn) back into the queue.
    if (
      (turn.status === 'running' && isUndeliveredTurnStatus(event.status)) ||
      (turn.status === 'admitted' && event.status === 'queued')
    ) {
      continue;
    }
    turn.status = event.status;
    if (isTerminalTurnStatus(event.status)) {
      turn.endedAt = event.at;
    }
  }
  turns.sort((a, b) => a.index - b.index || a.createdAt - b.createdAt);

  // Persisted user messages that belong to a turn (by inbox id or the
  // deterministic `msg_<turnId>`) don't render — the turn row owns the bubble.
  const userMessageTurn = new Map<string, TimelineTurn>();
  for (const turn of turns) {
    if (turn.inboxId !== null) {
      userMessageTurn.set(turn.inboxId, turn);
    }
    userMessageTurn.set(deterministicPromptId(turn.id), turn);
  }
  // OpenCode v2's ordered user-message boundaries identify which prompt each saved assistant
  // message answers. A queued prompt can be admitted without a matching
  // inbox.delivered event, leaving the live event owner pointing at the prior
  // turn; the saved boundary must take priority over that stale event owner.
  const savedOwnerByMessage = new Map<string, string>();
  let savedOwner: string | undefined;
  for (const message of [...input.messages].sort(
    (a, b) => messageCreatedAt(a) - messageCreatedAt(b),
  )) {
    if (message.type === 'user') {
      savedOwner = userMessageTurn.get(message.id)?.id;
    } else if (message.type === 'assistant' && savedOwner !== undefined) {
      savedOwnerByMessage.set(message.id, savedOwner);
    }
  }
  const agentEvents = [...input.agentEvents].sort((a, b) => a.seq - b.seq || a.at - b.at);
  const turnByMessage = new Map<string, string>();
  let deliveredTurnId: string | undefined;
  for (const event of agentEvents) {
    const data = eventData(event);
    if (event.type === 'session.inbox.delivered' && typeof data.inboxID === 'string') {
      deliveredTurnId = userMessageTurn.get(data.inboxID)?.id;
    } else if (typeof data.assistantMessageID === 'string' && deliveredTurnId !== undefined) {
      if (!turnByMessage.has(data.assistantMessageID)) {
        turnByMessage.set(data.assistantMessageID, deliveredTurnId);
      }
    }
  }
  const messageTurn = (messageId: string) =>
    savedOwnerByMessage.get(messageId) ?? turnByMessage.get(messageId);

  // Turns that pushed the branch or asked cubic to open its pull request. Each
  // ends with a result, the session's pull request, and the latest one keeps it.
  const publishedTurns = new Set<string>();
  for (const message of input.messages) {
    if (message.type !== 'assistant') {
      continue;
    }
    const turnId = messageTurn(message.id);
    const published = message.content.some(
      (content) =>
        content.type === 'tool' &&
        publishesBranch(content.name, 'input' in content.state ? content.state.input : undefined),
    );
    if (turnId !== undefined && published) {
      publishedTurns.add(turnId);
    }
  }
  // Live calls name their tool when they start and carry their input when called.
  const liveToolNames = new Map<string, string>();
  for (const event of agentEvents) {
    const data = eventData(event);
    const key = toolStreamKey(data);
    if (key === undefined) {
      continue;
    }
    if (event.type === 'session.tool.input.started' && typeof data.name === 'string') {
      liveToolNames.set(key, data.name);
    } else if (event.type === 'session.tool.called') {
      const name = liveToolNames.get(key);
      const turnId = messageTurn(data.assistantMessageID as string);
      if (name !== undefined && turnId !== undefined && publishesBranch(name, data.input)) {
        publishedTurns.add(turnId);
      }
    }
  }

  // A prompt sent while another turn runs (queued, or steered mid-step) is
  // answered only after that turn's remaining output, so its bubble belongs
  // where OpenCode delivered it: the saved user message's time, or the live
  // delivery event until that message is saved. Live events carry the time
  // cubic recorded them, so the bubble also never sorts after the first
  // content answering it. Before delivery the submission time keeps an
  // optimistic bubble in place; the bubble still shows when it was sent.
  const savedDeliveryAt = new Map<string, number>();
  const liveDeliveryAt = new Map<string, number>();
  const firstAnswerAt = new Map<string, number>();
  const noteEarliest = (times: Map<string, number>, turnId: string | undefined, at: number) => {
    if (turnId !== undefined && at < (times.get(turnId) ?? Number.POSITIVE_INFINITY)) {
      times.set(turnId, at);
    }
  };
  for (const message of input.messages) {
    // A `/compact` turn has no saved user message: OpenCode saves its compaction under the turn's
    // prompt ID when it delivers it.
    if (message.type === 'user' || message.type === 'compaction') {
      noteEarliest(savedDeliveryAt, userMessageTurn.get(message.id)?.id, messageCreatedAt(message));
    } else if (message.type === 'assistant') {
      noteEarliest(firstAnswerAt, messageTurn(message.id), messageCreatedAt(message));
    }
  }
  for (const event of agentEvents) {
    const data = eventData(event);
    if (event.type === 'session.inbox.delivered' && typeof data.inboxID === 'string') {
      noteEarliest(liveDeliveryAt, userMessageTurn.get(data.inboxID)?.id, event.at);
    } else if (typeof data.assistantMessageID === 'string') {
      noteEarliest(firstAnswerAt, messageTurn(data.assistantMessageID), event.at);
    }
  }
  const bubbleAt = (turn: TimelineTurn) => {
    // The agent keeps working until it takes a steer, so the steer stays below
    // everything it adds, rather than jumping down on delivery.
    if (input.pendingSteers?.has(turn.id)) {
      return Number.MAX_SAFE_INTEGER;
    }
    const deliveredAt = savedDeliveryAt.get(turn.id) ?? liveDeliveryAt.get(turn.id);
    return deliveredAt === undefined
      ? undeliveredBubbleAt(turn)
      : Math.min(deliveredAt, firstAnswerAt.get(turn.id) ?? deliveredAt);
  };

  const emittedTurns = new Set<string>();
  const emitUserBubble = (turn: TimelineTurn) => {
    if (emittedTurns.has(turn.id)) {
      return;
    }
    emittedTurns.add(turn.id);
    const pending = input.pendingSteers?.get(turn.id);
    parts.push({
      type: 'user',
      id: deterministicPromptId(turn.id),
      text: turn.prompt,
      authoredText: turn.authoredText,
      ...(turn.attachments.length ? { attachments: turn.attachments } : {}),
      ...(turn.checkInReason === null ? {} : { checkInReason: turn.checkInReason }),
      ...(turn.messageFrom === null ? {} : { messageFrom: turn.messageFrom }),
      ...(turn.authorProfileId === undefined ? {} : { authorProfileId: turn.authorProfileId }),
      createdAt: turn.createdAt,
      ...(pending ? { pending } : {}),
    });
  };

  const ctx: FoldContext = {
    persisted: persistedStreamKeys(input.messages),
    retries: responseRetryFold(input.messages, agentEvents),
    deltaParts: new Map(),
    endedStreams: new Set(),
    toolSteps: new Map(),
    traceByTool: new Map(),
    callCards: new Map(),
    codeMode: seedCodeModeScripts(input.messages),
    streamStartedAt: new Map(),
    runningByMessage: new Map(),
    stepMessages: new Set(),
    executionMessages: new Set(),
    messageParts: new Map(),
    partIds: new Map(),
    errors: new Map(),
    subscriptionErrors: new Map(),
    aiSubscriptionProvider: input.aiSubscriptionProvider,
    forms: new Set(),
    resolvedForms: new Set(),
    subagentActivity: new Map(),
  };

  // Timeline anchors: turn bubbles at delivery (submission until then),
  // persisted messages at their time.created, live events at their cubic
  // `at`, terminal turn events at `at`. Sorting by time yields the linear
  // chat order.
  type Anchor = { at: number; order: number; run: () => void; owner?: TimelineAnchorOwner };
  const anchors: Anchor[] = [];
  const visibleAssistantMessages: SessionMessageAssistant[] = [];
  let order = 0;
  const push = (at: number, run: () => void, owner?: TimelineAnchorOwner) => {
    anchors.push({ at, order: order++, run, owner });
  };

  for (const bootRow of bootRowAnchors(input, parts, ctx)) {
    push(bootRow.at, bootRow.run);
  }
  // A hidden prompt cubic never took, such as one removed from the queue, has no content after
  // it, so its place must not hide what the turn running then shows. That holds for its saved
  // user message too, which can exist before the turn row records a start.
  const hiddenUnstarted = (turn: TimelineTurn | undefined) =>
    turn !== undefined && hidden.has(turn.id) && turn.startedAt === undefined;
  for (const turn of turns) {
    if (hiddenUnstarted(turn)) {
      continue;
    }
    push(bubbleAt(turn), () => emitUserBubble(turn), { kind: 'prompt', turnId: turn.id });
  }
  for (const message of input.messages) {
    if (message.type === 'user') {
      const turn = userMessageTurn.get(message.id);
      if (hiddenUnstarted(turn)) {
        continue;
      }
      const at =
        turn === undefined
          ? (messageCreatedAtOverrides.get(message.id) ?? messageCreatedAt(message))
          : bubbleAt(turn);
      const emitPrompt = () => {
        ctx.retries.settle('stopped');
        if (turn !== undefined) {
          emitUserBubble(turn);
          return;
        }
        const text = userMessageText(message);
        if (text.length > 0) {
          parts.push({ type: 'user', id: message.id, text, createdAt: at || undefined });
        }
      };
      push(at, emitPrompt, { kind: 'prompt', turnId: turn?.id });
      continue;
    }
    const at = messageCreatedAt(message);
    if (message.type === 'assistant') {
      const foldMessage = () => {
        visibleAssistantMessages.push(message);
        const completed = assistantMessageCompleted(message);
        const messageFinish = message.finish;
        const messageCompletedAt = message.time.completed;
        // A reply closed with no finish reason was repaired after a kill; a stop closes it with
        // finish 'error', and a reply still open has no close at all.
        const repaired = completed && messageFinish === undefined;
        let previousWasTool = false;
        let textOrdinal = 0;
        let reasoningOrdinal = 0;
        for (const content of message.content) {
          const toolKey = content.type === 'tool' ? `${message.id}:tool:${content.id}` : undefined;
          if (toolKey !== undefined && ctx.retries.unexecutedTools.has(toolKey)) {
            continue;
          }
          // Its live events came first and already made its step.
          if (toolKey !== undefined && ctx.toolSteps.has(toolKey)) {
            previousWasTool = true;
            continue;
          }
          const part = foldAssistantContent(parts, content, completed, previousWasTool, repaired);
          if (part?.type === 'text') {
            part.createdAt = at || undefined;
            part.messageFinish = messageFinish;
            part.messageCompletedAt = messageCompletedAt;
          }
          if (part !== undefined) {
            const id =
              content.type === 'text'
                ? `${message.id}:text:${textOrdinal}`
                : content.type === 'reasoning'
                  ? `${message.id}:reasoning:${reasoningOrdinal}`
                  : content.type === 'tool' && !isQuestionTool(content)
                    ? toolKey
                    : undefined;
            registerMessagePart(ctx, message.id, part, id);
            // A part this unfinished message saved empty streams in place.
            if (
              id !== undefined &&
              (part.type === 'text' || part.type === 'reasoning') &&
              !ctx.persisted.has(id)
            ) {
              ctx.deltaParts.set(id, part);
            }
            // So does a step saved before its call finished; it is the one just added.
            // A finished script recovers richer progress metadata in its separate fold.
            const step = part.type === 'trace' ? part.steps.at(-1) : undefined;
            if (
              content.type === 'tool' &&
              toolKey !== undefined &&
              step !== undefined &&
              !ctx.persisted.has(toolKey)
            ) {
              ctx.toolSteps.set(toolKey, { name: content.name, step });
              ctx.traceByTool.set(toolKey, part);
            }
          }
          if (content.type === 'text') {
            textOrdinal += 1;
          } else if (content.type === 'reasoning') {
            reasoningOrdinal += 1;
          }
          // Consecutive trace tools share a trace; anything else, a card tool included,
          // ends it. A suggestion or page call that showed nothing (it errored) and a result or
          // batch call leave the trace as it was, as they do while streaming.
          const showedNothing =
            content.type === 'tool' &&
            (isResultTool(content) || (isCallCardTool(content) && part === undefined));
          if (!showedNothing) {
            previousWasTool = content.type === 'tool' && !isCardTool(content);
          }
          if (!completed && hasRunningState(part) && part.running === true) {
            trackRunning(ctx, message.id, part);
          }
        }
        const error = isAbortedError(message.error)
          ? undefined
          : transcriptErrorOf(message.error, ctx);
        if (error !== undefined) {
          if (
            !pushResponseRetry(parts, ctx, message.id, message.error, {
              at: message.time.completed ?? at,
            })
          ) {
            ctx.retries.settle('stopped', ctx.errorScope);
            pushError(parts, ctx, error);
          }
        } else if (message.error === undefined && message.retry !== undefined) {
          pushResponseRetry(parts, ctx, message.id, message.retry.error);
        } else if (isAbortedError(message.error)) {
          ctx.retries.settle('stopped', ctx.errorScope);
        }
      };
      push(at, foldMessage, { kind: 'message', messageId: message.id });
      if (
        message.time.completed !== undefined &&
        message.error === undefined &&
        successfulResponseFinish(message.finish)
      ) {
        push(message.time.completed, () => ctx.retries.settle('recovered', ctx.errorScope), {
          kind: 'message',
          messageId: message.id,
        });
      }
      continue;
    }
    if (message.type === 'idle') {
      push(at, () => {
        if (message.outcome === 'failed') {
          const error = transcriptErrorOf(ctx.retries.pendingError(), ctx);
          if (error !== undefined) {
            pushError(parts, ctx, error);
          }
        }
        ctx.retries.settle(message.outcome === 'succeeded' ? 'recovered' : 'stopped');
      });
    }
    if (message.type === 'compaction' && message.status === 'failed') {
      push(at, () => pushError(parts, ctx, { message: 'Context compaction failed' }));
    }
    // A `/compact` turn's bubble already has the compaction's id, which is the turn's prompt id.
    if (message.type === 'compaction' && message.status === 'completed') {
      push(at, () => {
        parts.push({ type: 'status', id: `${message.id}:compacted`, label: 'Context compacted' });
      });
    }
  }
  for (const event of agentEvents) {
    const data = eventData(event);
    const messageId = data.assistantMessageID;
    const owner: TimelineAnchorOwner | undefined =
      typeof messageId === 'string' ? { kind: 'message', messageId } : undefined;
    const at =
      event.type === 'session.retry.scheduled' && typeof data.at === 'number'
        ? Math.min(event.at, data.at)
        : event.at;
    push(at, () => foldAgentEvent(parts, event, ctx), owner);
  }
  const turnEvents = [...(input.turnEvents ?? [])].sort((a, b) => a.seq - b.seq);
  // What each turn shared, from its row or its end event, which goes out first.
  const endedSharedFiles = listsByTurn(
    turns,
    turnEvents,
    (turn) => turn.sharedFiles,
    (event) => event.sharedFiles,
  );
  const emittedResults = new Set<string>();
  const sentByCubic = new Set(turns.filter((turn) => !isPersonTurn(turn)).map((turn) => turn.id));
  const emitTurnTerminal = (turnId: string, status: string, errorMessage?: string) => {
    // Under the turn's last reply, which refers to them, and above its result
    // or error. A stopped or failed turn keeps what it shared; a turn's start
    // event also comes through here, long before any of it.
    const ended = isTerminalTurnStatus(status);
    if (ended) {
      ctx.retries.settle(status === 'done' ? 'recovered' : 'stopped', turnId);
    }
    const shared = endedSharedFiles.get(turnId);
    if (ended && shared !== undefined && !emittedResults.has(`${turnId}:shared`)) {
      emittedResults.add(`${turnId}:shared`);
      parts.push({ type: 'shared_files', turnId, files: shared });
    }
    // An intentional Stop already has a neutral activity status. Only show a
    // red card when the interrupted turn carries a real error explanation.
    const terminalError =
      status === 'failed'
        ? (turnFailureWords(errorMessage) ?? 'The turn failed')
        : status === 'interrupted' && errorMessage?.trim()
          ? errorMessage
          : undefined;
    if (terminalError !== undefined && !emittedResults.has(`${turnId}:err`)) {
      emittedResults.add(`${turnId}:err`);
      const error = pushError(parts, ctx, { message: terminalError });
      if (!sentByCubic.has(turnId)) {
        error.turnId = turnId;
      }
      return;
    }
    if (
      status === 'done' &&
      publishedTurns.has(turnId) &&
      !emittedResults.has(`${turnId}:result`)
    ) {
      emittedResults.add(`${turnId}:result`);
      parts.push({ type: 'result', turnId });
    }
  };
  // Row state first (historic turns), then events override at their time.
  for (const turn of turns) {
    if (isTerminalTurnStatus(turn.status)) {
      const at = turn.endedAt ?? turn.createdAt;
      const emitTerminal = () =>
        emitTurnTerminal(turn.id, turn.status, turn.errorMessage ?? undefined);
      push(at, emitTerminal, { kind: 'turn', turnId: turn.id });
    }
  }
  for (const event of turnEvents) {
    const emitTerminal = () => emitTurnTerminal(event.turnId, event.status, event.errorMessage);
    push(event.at, emitTerminal, { kind: 'turn', turnId: event.turnId });
  }
  // Forms pending in the transcript snapshot surface at the end of history.
  for (const form of input.forms ?? []) {
    push(Number.MAX_SAFE_INTEGER - 1, () => {
      if (ctx.resolvedForms.has(form.id) || ctx.forms.has(form.id)) {
        return;
      }
      ctx.forms.add(form.id);
      parts.push(formQuestionPart(form));
    });
  }

  // Older callers without a turn ID still retain their original submission position.
  for (const pending of input.pendingPrompts ?? []) {
    if (pending.turnId === undefined) {
      const emitPending = () =>
        parts.push({
          type: 'user',
          id: pending.clientId,
          text: pending.text,
          authoredText: pending.authoredText,
          ...(pending.attachments.length ? { attachments: pending.attachments } : {}),
          ...(input.promptAuthorId === undefined ? {} : { authorProfileId: input.promptAuthorId }),
          createdAt: pending.createdAt,
        });
      push(pending.createdAt, emitPending, { kind: 'prompt' });
    }
  }

  anchors.sort((a, b) => a.at - b.at || a.order - b.order);
  const errorOwners = new Set([
    ...turns.map((turn) => turn.id),
    ...(input.turnEvents ?? []).map((event) => event.turnId),
  ]);
  let currentPrompt = errorOwners.size === 1 ? errorOwners.values().next().value : undefined;
  for (const anchor of visibleTimelineAnchors(anchors, hidden, messageTurn)) {
    const owner = anchor.owner;
    if (owner?.kind === 'prompt') {
      currentPrompt = owner.turnId;
    }
    switch (owner?.kind) {
      case 'turn':
        ctx.errorScope = owner.turnId;
        break;
      case 'message':
        ctx.errorScope = messageTurn(owner.messageId) ?? currentPrompt;
        break;
      default:
        ctx.errorScope = currentPrompt;
    }
    anchor.run();
  }

  // A verdict that something is not dead code takes it off its cards' removal.
  markKeptFindings(parts, visibleAssistantMessages);
  const hasFormQuestion = ctx.forms.size > 0 || ctx.resolvedForms.size > 0;
  // The pull request card follows the pull request to the latest push.
  const latestResult = parts.findLast((part) => part.type === 'result');
  const visible = parts.filter((part) => {
    if (part.type === 'result') {
      return part === latestResult;
    }
    if (part.type === 'reasoning' && part.running !== true && part.text.trim().length === 0) {
      return false;
    }
    if (part.type !== 'question') {
      return true;
    }
    if (part.formId !== undefined) {
      return !ctx.resolvedForms.has(part.formId);
    }
    return !hasFormQuestion;
  });
  // A pending permission request names the call it holds up: that step waits for the person's
  // answer on the permission card, whatever OpenCode's tool state says meanwhile.
  const awaitingApproval = new Set(
    visible.flatMap((part) =>
      part.type === 'question' && part.permission?.callId !== undefined
        ? [part.permission.callId]
        : [],
    ),
  );
  if (awaitingApproval.size > 0) {
    for (const part of visible) {
      if (part.type !== 'trace') {
        continue;
      }
      for (const step of part.steps) {
        if (step.callId !== undefined && awaitingApproval.has(step.callId)) {
          step.waitingApproval = true;
          step.running = undefined;
        }
      }
    }
  }
  const visibleParts = new Set(visible);
  const activeTurn = turns.filter((turn) => turn.status === 'running').at(-1);
  const turnById = new Map(turns.map((turn) => [turn.id, turn]));
  const persistedMessageAt = new Map(
    input.messages.map((message) => [message.id, messageCreatedAt(message)]),
  );
  for (const [messageId, messageParts] of ctx.messageParts) {
    const at = persistedMessageAt.get(messageId);
    const temporalTurn =
      at === undefined
        ? undefined
        : [...turns]
            .reverse()
            .find(
              (turn) =>
                turn.startedAt !== undefined &&
                turn.startedAt <= at &&
                (turn.endedAt === undefined || turn.endedAt === null || at <= turn.endedAt),
            );
    const turn = turnById.get(messageTurn(messageId) ?? temporalTurn?.id ?? '');
    if (turn === undefined) {
      continue;
    }
    const ordinals = new Map<string, number>();
    for (const part of messageParts) {
      const ordinal = ordinals.get(part.type) ?? 0;
      ordinals.set(part.type, ordinal + 1);
      if (!visibleParts.has(part)) {
        continue;
      }
      part.id = ctx.partIds.get(part) ?? `${messageId}:${part.type}:${ordinal}`;
      part.messageId = messageId;
      part.turn = {
        id: turn.id,
        status: turn.status,
        ...(turn.startedAt === undefined ? {} : { startedAt: turn.startedAt }),
        ...(turn.endedAt == null ? {} : { endedAt: turn.endedAt }),
      };
      part.active = turn.id === activeTurn?.id;
      settleEndedTurnTrace(part, turn.status);
    }
  }
  showCodeModeScripts(ctx.messageParts, ctx.codeMode);
  // Once liveness is final: only a call that still runs shows what its child does.
  showSubagentActivity(
    visible.flatMap((part) => (part.type === 'trace' ? part.steps : [])),
    ctx.subagentActivity,
  );
  return withoutRedoneSteps(visible);
}

/**
 * A call cut short reads as the agent goes on with it. Made again later in its turn, it shows
 * once, as the call made again: an agent that moves into its checkout's sandbox (`agent-move.ts`)
 * makes again the call the move interrupted, in its next message. Until then, while its turn
 * runs and nothing follows it, it still reads as running; its turn's end settles it.
 */
function withoutRedoneSteps(parts: SessionPart[]): SessionPart[] {
  parts.forEach((part, index) => {
    if (part.type !== 'trace' || !part.steps.some((step) => step.interrupted)) {
      return;
    }
    const later = laterPartsOfTurn(parts, index);
    const laterSteps = later.flatMap((entry) => (entry.type === 'trace' ? entry.steps : []));
    part.steps = part.steps.filter(
      (step, stepIndex) =>
        !step.interrupted ||
        ![...part.steps.slice(stepIndex + 1), ...laterSteps].some(
          (redone) =>
            !redone.interrupted &&
            redone.kind === step.kind &&
            redone.label === step.label &&
            redone.detail === step.detail,
        ),
    );
    const last = part.steps.at(-1);
    if (part.active === true && last?.interrupted === true && later.length === 0) {
      last.running = true;
      part.running = true;
    }
  });
  return parts.filter((part) => part.type !== 'trace' || part.steps.length > 0);
}

/**
 * The parts after `parts[index]` in its turn, up to the next turn's: those the chat has not tied
 * to a turn count too, such as the answer the agent goes on with after a move's note. Without a
 * turn, the traces that follow it at once.
 */
function laterPartsOfTurn(parts: SessionPart[], index: number): SessionPart[] {
  const turnId = parts[index]?.turn?.id;
  const later = parts.slice(index + 1);
  const end = later.findIndex((part) =>
    turnId === undefined
      ? part.type !== 'trace'
      : part.turn !== undefined && part.turn.id !== turnId,
  );
  return end === -1 ? later : later.slice(0, end);
}

/**
 * Nothing of an ended turn runs, whatever its message last said, such as a call its stopped
 * sandbox never finished: the chat and the subagent panel read one liveness from the steps.
 */
function settleEndedTurnTrace(part: SessionPart, turnStatus: string): void {
  if (part.type !== 'trace' || !isTerminalTurnStatus(turnStatus)) {
    return;
  }
  part.running = undefined;
  for (const step of part.steps) {
    step.running = undefined;
  }
}
